/**
 * Reading HTTP status codes back off a rejected query.
 *
 * The generated clients each throw their own exception class (`ShopsApiException`,
 * `OrdersApiException`, `PaymentsApiException`, …), but all of them carry a numeric `status`, and
 * anything thrown by axios directly carries `response.status`. Checking the shape rather than the
 * class keeps this working for every API without importing — and without growing — a list of them.
 */
const statusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const candidate = error as { status?: unknown; response?: { status?: unknown } };
  if (typeof candidate.status === 'number') {
    return candidate.status;
  }

  return typeof candidate.response?.status === 'number' ? candidate.response.status : undefined;
};

/** The server understood and refused: this user may not see this data. */
export const isForbiddenError = (error: unknown): boolean => statusOf(error) === 403;

/** The server has no such resource — for a lookup by id, an answer of "there is none" rather than a failure. */
export const isNotFoundError = (error: unknown): boolean => statusOf(error) === 404;

/**
 * Worth giving up on rather than retrying: anything in the 4xx range.
 *
 * A client error is an answer, not a blip. Asking three more times cannot turn "you may not" into
 * "you may", nor a bad request into a good one — it only delays telling the user by three round
 * trips and leaves them watching a spinner meanwhile. 5xx and connection failures are the ones a
 * retry can actually fix, and those still get the full count.
 */
export const isClientError = (error: unknown): boolean => {
  const status = statusOf(error);

  return status !== undefined && status >= 400 && status < 500;
};
